import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/use-toast';

export interface Company {
  id: string;
  name: string;
  description?: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyMember {
  id: string;
  company_id: string;
  user_id: string;
  role: 'admin' | 'member' | 'vendeur';
  invited_by?: string;
  joined_at: string;
  profile?: {
    full_name?: string;
  };
}

export interface TeamInvitation {
  id: string;
  company_id: string;
  email: string;
  role: 'admin' | 'member' | 'vendeur';
  invited_by?: string | null;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  token_hash: string;
  expires_at: string;
  created_at: string;
  accepted_at?: string | null;
}

interface CompanyContextType {
  currentCompany: Company | null;
  userRole: 'admin' | 'member' | 'vendeur' | null;
  companies: Company[];
  members: CompanyMember[];
  loading: boolean;
  createCompany: (name: string, description?: string) => Promise<boolean>;
  switchCompany: (companyId: string) => Promise<boolean>;
  inviteMember: (email: string, role?: string) => Promise<boolean>;
  removeMember: (memberId: string) => Promise<boolean>;
  fetchMembers: () => Promise<void>;
  refreshData: () => Promise<void>;
  invitations: TeamInvitation[];
  fetchInvitations: () => Promise<void>;
  cancelInvitation: (invitationId: string) => Promise<boolean>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'member' | 'vendeur' | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      initializeCompanyData();
    } else {
      setCurrentCompany(null);
      setUserRole(null);
      setCompanies([]);
      setMembers([]);
      setInvitations([]);
      setLoading(false);
    }
  }, [user]);

  const initializeCompanyData = async () => {
    try {
      setLoading(true);
      
      // Get user's profile to see current company
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user!.id)
        .single();

      // Get all companies user belongs to
      const { data: userCompanies } = await supabase
        .from('company_members')
        .select(`
          role,
          companies:company_id (
            id,
            name,
            description,
            owner_id,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', user!.id);

      if (userCompanies && userCompanies.length > 0) {
        const companiesList = userCompanies.map(uc => uc.companies).filter(Boolean) as Company[];
        setCompanies(companiesList);

        // Set current company (from profile or first available)
        let currentComp = null;
        let currentRole = null;

        if (profile?.company_id) {
          currentComp = companiesList.find(c => c.id === profile.company_id);
          const membership = userCompanies.find(uc => uc.companies?.id === profile.company_id);
          currentRole = membership?.role || null;
        }

        if (!currentComp && companiesList.length > 0) {
          currentComp = companiesList[0];
          currentRole = userCompanies[0].role;
          
          // Update profile with first company
          await supabase
            .from('profiles')
            .update({ company_id: currentComp.id })
            .eq('user_id', user!.id);
        }

        setCurrentCompany(currentComp);
        setUserRole(currentRole);

        if (currentComp) {
          await fetchMembers();
          if (userRole === 'admin') {
            await fetchInvitations();
          }
        }
      } else {
        // No companies, user needs to create one
        setCurrentCompany(null);
        setUserRole(null);
      }
    } catch (error) {
      console.error('Error initializing company data:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données de l'entreprise",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createCompany = async (name: string, description?: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('create_company_with_owner', {
        company_name: name,
        company_description: description || null
      });

      if (error) throw error;

      await refreshData();
      
      toast({
        title: "Succès",
        description: `Entreprise "${name}" créée avec succès`,
      });

      return true;
    } catch (error) {
      console.error('Error creating company:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer l'entreprise",
        variant: "destructive",
      });
      return false;
    }
  };

  const switchCompany = async (companyId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ company_id: companyId })
        .eq('user_id', user!.id);

      if (error) throw error;

      await refreshData();
      
      toast({
        title: "Succès",
        description: "Entreprise changée avec succès",
      });

      return true;
    } catch (error) {
      console.error('Error switching company:', error);
      toast({
        title: "Erreur",
        description: "Impossible de changer d'entreprise",
        variant: "destructive",
      });
      return false;
    }
  };

  const inviteMember = async (email: string, role: string = 'member'): Promise<boolean> => {
    try {
      const { data: plainToken, error } = await supabase.rpc('send_team_invitation', {
        p_email: email,
        p_role: role
      });

      if (error) throw error;

      // Si la fonction retourne un token, c'est qu'une nouvelle invitation a été créée
      if (plainToken && currentCompany) {
        await supabase.functions.invoke('send-invite-email', {
          body: {
            email,
            token: plainToken,
            companyName: currentCompany.name,
            inviterEmail: user?.email,
          }
        });
      }

      // Rafraîchir la liste des membres et invitations après l'invitation
      await fetchMembers();
      await fetchInvitations();

      toast({
        title: "Invitation envoyée",
        description: `Une invitation a été envoyée à ${email}.`,
        variant: "default",
      });
      return true;
    } catch (error) {
      console.error('Error inviting member:', error);
      let errorMessage = "Impossible d'inviter le membre";
      
      // Améliorer les messages d'erreur spécifiques
      if (error instanceof Error) {
        if (error.message.includes("appartient déjà à une entreprise")) {
          errorMessage = "Cette personne appartient déjà à une entreprise. Un utilisateur ne peut être membre que d'une seule entreprise à la fois.";
        } else if (error.message.includes("Only admins can invite")) {
          errorMessage = "Seuls les administrateurs peuvent envoyer des invitations.";
        } else if (error.message.includes("not authenticated")) {
          errorMessage = "Vous devez être connecté pour envoyer des invitations.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  };
  const removeMember = async (memberId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('company_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      await fetchMembers();
      
      toast({
        title: "Succès",
        description: "Membre retiré avec succès",
      });

      return true;
    } catch (error) {
      console.error('Error removing member:', error);
      toast({
        title: "Erreur",
        description: "Impossible de retirer le membre",
        variant: "destructive",
      });
      return false;
    }
  };

  const fetchMembers = async () => {
    if (!currentCompany) return;

    try {
      const { data, error } = await supabase
        .from('company_members')
        .select(`
          id,
          company_id,
          user_id,
          role,
          invited_by,
          joined_at
        `)
        .eq('company_id', currentCompany.id);

      if (error) throw error;

      // Use secure function to get team member profiles with masked emails
      const { data: profiles } = await supabase
        .rpc('get_masked_team_profiles', { p_company_id: currentCompany.id });

      const membersWithProfiles: CompanyMember[] = data?.map(member => ({
        ...member,
        role: member.role as 'admin' | 'member' | 'vendeur',
        profile: profiles?.find(p => p.user_id === member.user_id)
      })) || [];

      setMembers(membersWithProfiles);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchInvitations = async () => {
    if (!currentCompany) return;

    try {
      const { data, error } = await supabase
        .from('team_invitations')
        .select('id, company_id, email, role, invited_by, status, token_hash, expires_at, created_at, accepted_at')
        .eq('company_id', currentCompany.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setInvitations((data || []) as TeamInvitation[]);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const cancelInvitation = async (invitationId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('team_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;

      await fetchInvitations();

      toast({
        title: "Invitation annulée",
        description: "L'invitation a été supprimée.",
      });

      return true;
    } catch (error) {
      console.error('Error canceling invitation:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'annuler l'invitation",
        variant: "destructive",
      });
      return false;
    }
  };

  const refreshData = async () => {
    await initializeCompanyData();
  };

  return (
    <CompanyContext.Provider value={{
      currentCompany,
      userRole,
      companies,
      members,
      loading,
      createCompany,
      switchCompany,
      inviteMember,
      removeMember,
      fetchMembers,
      refreshData,
      invitations,
      fetchInvitations,
      cancelInvitation,
    }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};
